<script lang="ts">
import type { NxRecurrenceEnd, NxRecurrenceFrequency } from './recurrence';

/** Every caption of the editor; German defaults, apps pass their i18n text. */
export interface NxRecurrenceLabels {
  frequency: string;
  frequencies: Record<NxRecurrenceFrequency, string>;
  interval: string;
  /** Unit after the interval, singular and plural. */
  units: Record<Exclude<NxRecurrenceFrequency, 'none'>, [one: string, many: string]>;
  weekdays: string;
  monthlyBy: string;
  monthlyByDay: string;
  monthlyByWeekday: string;
  monthDay: string;
  position: string;
  /** Keys: 1–4 and -1 (the last). */
  positions: Record<number, string>;
  weekday: string;
  months: string;
  end: string;
  ends: Record<NxRecurrenceEnd, string>;
  count: string;
  until: string;
  /** Heading of the `occurrences` list. */
  occurrences: string;
}

export const NX_RECURRENCE_LABELS: NxRecurrenceLabels = {
  frequency: 'Wiederholung',
  frequencies: {
    none: 'Keine Wiederholung',
    daily: 'Täglich',
    weekly: 'Wöchentlich',
    monthly: 'Monatlich',
    yearly: 'Jährlich',
  },
  interval: 'Alle',
  units: {
    daily: ['Tag', 'Tage'],
    weekly: ['Woche', 'Wochen'],
    monthly: ['Monat', 'Monate'],
    yearly: ['Jahr', 'Jahre'],
  },
  weekdays: 'An diesen Tagen',
  monthlyBy: 'Monatlich am',
  monthlyByDay: 'Tag im Monat',
  monthlyByWeekday: 'Wochentag im Monat',
  monthDay: 'Tag',
  position: 'Welcher',
  positions: { 1: 'Ersten', 2: 'Zweiten', 3: 'Dritten', 4: 'Vierten', [-1]: 'Letzten' },
  weekday: 'Wochentag',
  months: 'In diesen Monaten (keiner = Monat des Beginns)',
  end: 'Ende',
  ends: { never: 'Nie', count: 'Nach Anzahl', until: 'Am Datum' },
  count: 'Anzahl Termine',
  until: 'Letzter Tag',
  occurrences: 'Nächste Termine',
};
</script>

<script setup lang="ts">
/**
 * Editor for a repeat rule: none / daily / weekly / monthly / yearly, the
 * interval („alle 2 Wochen“), the weekdays (NxWeekdayPicker, RRULE codes),
 * monthly by the day of the month or by the nth weekday, the months of a
 * yearly rule, and the end — never, after N occurrences, or on a date
 * (NxDateInput). Below it the rule reads as one sentence
 * (`describeRecurrence`: „Jede 2. Woche am Mo und Mi, bis 31.12.2026“).
 *
 * `v-model` is an `NxRecurrenceRule` (RRULE-shaped, no calendar types).
 * `startDate` is the first occurrence: it fills the defaults (weekday, day of
 * the month) and is the earliest end date. The caller may pass the next
 * `occurrences` it expands from the rule; they are listed under the summary.
 */
import Repeat from 'lucide-vue-next/dist/esm/icons/repeat.js';
import { computed, inject, useId } from 'vue';
import NxChip from '../Chip/NxChip.vue';
import { formatDate, formatTime, parseDateLike } from '../Format/date';
import NxDateInput from '../Inputs/NxDateInput.vue';
import NxLabel from '../Inputs/NxLabel.vue';
import NxNativeSelect from '../Inputs/NxNativeSelect.vue';
import NxNumberInput, { UINT_PATTERN_NO_ZERO } from '../Inputs/NxNumberInput.vue';
import { i18nInjectionKey } from '../internal';
import type { NxDateLike } from '../RelativeTime/relativeTime';
import NxToggleGroup from '../ToggleGroup/NxToggleGroup.vue';
import NxWeekdayPicker from '../WeekdayPicker/NxWeekdayPicker.vue';
import { weekdayNames, weekdayOrder } from '../WeekdayPicker/weekdays';
import {
  createRecurrenceRule,
  describeRecurrence,
  isMonthlyByWeekday,
  NX_RECURRENCE_FREQUENCIES,
  NX_RECURRENCE_POSITIONS,
  recurrenceEnd,
  toIsoDate,
  weekdayCode,
  weekdayFromCode,
  weekdayPosition,
  type NxRecurrenceRule,
} from './recurrence';

const props = withDefaults(
  defineProps<{
    /** The first occurrence: defaults for new rules, earliest end date. */
    startDate?: NxDateLike;
    /** BCP 47 locale for day and month names and the summary; the app's locale when omitted. */
    locale?: string;
    /** First day of the weekday row: 1 = Monday (default), 0 = Sunday. */
    firstDay?: number;
    /** The frequencies offered, in this order. */
    frequencies?: readonly NxRecurrenceFrequency[];
    labels?: Partial<NxRecurrenceLabels>;
    /** Replaces the generated summary sentence. */
    summary?: string;
    showSummary?: boolean;
    /** Offer the month chips of a yearly rule. */
    showMonths?: boolean;
    /** The next occurrences, computed by the caller — listed under the summary. */
    occurrences?: readonly NxDateLike[];
    /** Show the time of day of each occurrence (off for all-day series). */
    occurrencesTime?: boolean;
    disabled?: boolean;
  }>(),
  {
    startDate: undefined,
    locale: undefined,
    firstDay: 1,
    frequencies: () => NX_RECURRENCE_FREQUENCIES,
    labels: () => ({}),
    summary: undefined,
    showSummary: true,
    showMonths: true,
    occurrences: () => [],
    occurrencesTime: true,
    disabled: false,
  }
);

const model = defineModel<NxRecurrenceRule>({ default: () => createRecurrenceRule() });

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value ?? 'de-DE');
const text = computed<NxRecurrenceLabels>(() => ({ ...NX_RECURRENCE_LABELS, ...props.labels }));
const uid = useId();

const rule = computed(() => model.value ?? createRecurrenceRule());
const active = computed(() => rule.value.frequency !== 'none');
const start = computed(() => parseDateLike(props.startDate ?? null));
const startIso = computed(() => (start.value ? toIsoDate(start.value) : undefined));

function patch(partial: Partial<NxRecurrenceRule>) {
  model.value = { ...createRecurrenceRule(), ...rule.value, ...partial };
}

// ── Frequency and interval ──────────────────────────────────────────────────
const frequencyOptions = computed(() =>
  props.frequencies.map((value) => ({ value, label: text.value.frequencies[value] }))
);

function setFrequency(value: string | number | null) {
  const frequency = (value ?? 'none') as NxRecurrenceFrequency;
  if (frequency === rule.value.frequency) return;
  patch({
    frequency,
    byDay: frequency === 'weekly' && start.value ? [weekdayCode(start.value.getDay())] : [],
    byMonthDay: null,
    bySetPosition: [],
    byMonth: [],
  });
}

const interval = computed(() => Math.max(1, Math.trunc(Number(rule.value.interval) || 1)));
const unit = computed(() => {
  const frequency = rule.value.frequency;
  if (frequency === 'none') return '';
  const [one, many] = text.value.units[frequency];
  return interval.value === 1 ? one : many;
});

function setInterval(value: string | number) {
  const n = Math.trunc(Number(value));
  if (Number.isFinite(n) && n >= 1) patch({ interval: n });
}

// ── Weekly ──────────────────────────────────────────────────────────────────
const pickedDays = computed(() =>
  (rule.value.byDay ?? []).map(weekdayFromCode).filter((day) => day >= 0)
);

function setDays(value: number | number[] | null) {
  const days = Array.isArray(value) ? value : value === null ? [] : [value];
  patch({ byDay: days.map(weekdayCode) });
}

// ── Monthly ─────────────────────────────────────────────────────────────────
type MonthlyMode = 'day' | 'weekday';
const monthlyMode = computed<MonthlyMode>(() =>
  isMonthlyByWeekday(rule.value) ? 'weekday' : 'day'
);
const monthlyOptions = computed(() => [
  { value: 'day' as const, label: text.value.monthlyByDay },
  { value: 'weekday' as const, label: text.value.monthlyByWeekday },
]);

const position = computed(() => rule.value.bySetPosition?.[0] ?? 1);
const monthlyWeekday = computed(() => {
  const day = weekdayFromCode(rule.value.byDay?.[0] ?? '');
  return day >= 0 ? day : (start.value?.getDay() ?? 1);
});

function setMonthlyMode(mode: MonthlyMode) {
  if (mode === monthlyMode.value) return;
  if (mode === 'day') {
    patch({ byDay: [], bySetPosition: [] });
    return;
  }
  const from = start.value;
  patch({
    byMonthDay: null,
    byDay: [weekdayCode(from ? from.getDay() : 1)],
    bySetPosition: [from ? weekdayPosition(from) : 1],
  });
}

function setMonthDay(value: string | number) {
  if (value === '') {
    patch({ byMonthDay: null });
    return;
  }
  const n = Math.trunc(Number(value));
  if (Number.isFinite(n)) patch({ byMonthDay: Math.min(31, Math.max(1, n)) });
}

const positionOptions = computed(() =>
  NX_RECURRENCE_POSITIONS.map((value) => ({
    value,
    label: text.value.positions[value] ?? String(value),
  }))
);
const weekdayOptions = computed(() => {
  const names = weekdayNames(locale.value, 'long');
  return weekdayOrder(props.firstDay).map((day) => ({ value: day, label: names[day]! }));
});

// ── Yearly ──────────────────────────────────────────────────────────────────
const monthChips = computed(() => {
  const format = new Intl.DateTimeFormat(locale.value, { month: 'short', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, m) => ({
    value: m + 1,
    label: format.format(new Date(Date.UTC(2024, m, 1))).replace(/\.$/, ''),
  }));
});

function toggleMonth(month: number) {
  const current = rule.value.byMonth ?? [];
  const next = current.includes(month)
    ? current.filter((m) => m !== month)
    : [...current, month].sort((a, b) => a - b);
  patch({ byMonth: next });
}

// ── End ─────────────────────────────────────────────────────────────────────
const end = computed(() => recurrenceEnd(rule.value));
const endOptions = computed(() =>
  (['never', 'count', 'until'] as const).map((value) => ({ value, label: text.value.ends[value] }))
);

function setEnd(mode: NxRecurrenceEnd) {
  if (mode === end.value) return;
  if (mode === 'never') patch({ count: null, until: null });
  else if (mode === 'count') patch({ count: 10, until: null });
  else patch({ count: null, until: startIso.value ?? toIsoDate(new Date()) });
}

function setCount(value: string | number) {
  const n = Math.trunc(Number(value));
  if (Number.isFinite(n) && n >= 1) patch({ count: n, until: null });
}

function setUntil(value: string | null) {
  if (value) patch({ until: value, count: null });
}

// ── Summary ─────────────────────────────────────────────────────────────────
const summaryText = computed(
  () =>
    props.summary ??
    describeRecurrence(rule.value, { locale: locale.value, startDate: props.startDate })
);

const occurrenceLines = computed(() =>
  props.occurrences
    .map((value) => parseDateLike(value))
    .filter((date): date is Date => date !== null)
    .map((date) => {
      const day = formatDate(date, { style: 'full', locale: locale.value });
      return {
        key: date.getTime(),
        text: props.occurrencesTime
          ? `${day} · ${formatTime(date, { locale: locale.value })}`
          : day,
      };
    })
);
</script>

<template>
  <div
    class="nx-recurrence-input"
    :class="{ 'is-disabled': disabled }"
    data-testid="nx-recurrence-input"
  >
    <div class="nx-recurrence-input__row">
      <div class="nx-recurrence-input__field">
        <NxLabel :text="text.frequency">
          <NxNativeSelect
            :model-value="rule.frequency"
            :options="frequencyOptions"
            :disabled="disabled"
            size="full"
            data-part="frequency"
            @update:model-value="setFrequency"
          />
        </NxLabel>
      </div>
      <div v-if="active" class="nx-recurrence-input__field is-number" data-part="interval">
        <NxLabel :text="text.interval">
          <NxNumberInput
            :model-value="interval"
            :pattern="UINT_PATTERN_NO_ZERO"
            inputmode="numeric"
            :disabled="disabled"
            @update:model-value="setInterval"
          />
        </NxLabel>
      </div>
      <span v-if="active" class="nx-recurrence-input__unit">{{ unit }}</span>
    </div>

    <div v-if="rule.frequency === 'weekly'" class="nx-recurrence-input__block">
      <span class="nx-recurrence-input__caption">{{ text.weekdays }}</span>
      <NxWeekdayPicker
        multiple
        :model-value="pickedDays"
        :first-day="firstDay"
        :locale="locale"
        :disabled="disabled"
        :aria-label="text.weekdays"
        @update:model-value="setDays"
      />
    </div>

    <div v-if="rule.frequency === 'monthly'" class="nx-recurrence-input__block">
      <NxToggleGroup
        :model-value="monthlyMode"
        :options="monthlyOptions"
        :label="text.monthlyBy"
        :disabled="disabled"
        data-part="monthly-mode"
        @update:model-value="setMonthlyMode"
      />
      <div v-if="monthlyMode === 'day'" class="nx-recurrence-input__row">
        <div class="nx-recurrence-input__field is-number" data-part="month-day">
          <NxLabel :text="text.monthDay">
            <NxNumberInput
              :model-value="rule.byMonthDay ?? ''"
              :placeholder="start ? String(start.getDate()) : undefined"
              :pattern="UINT_PATTERN_NO_ZERO"
              inputmode="numeric"
              :disabled="disabled"
              @update:model-value="setMonthDay"
            />
          </NxLabel>
        </div>
      </div>
      <div v-else class="nx-recurrence-input__row">
        <div class="nx-recurrence-input__field">
          <NxLabel :text="text.position">
            <NxNativeSelect
              :model-value="position"
              :options="positionOptions"
              :disabled="disabled"
              size="full"
              data-part="position"
              @update:model-value="patch({ bySetPosition: [Number($event ?? 1)] })"
            />
          </NxLabel>
        </div>
        <div class="nx-recurrence-input__field">
          <NxLabel :text="text.weekday">
            <NxNativeSelect
              :model-value="monthlyWeekday"
              :options="weekdayOptions"
              :disabled="disabled"
              size="full"
              data-part="weekday"
              @update:model-value="patch({ byDay: [weekdayCode(Number($event ?? 1))] })"
            />
          </NxLabel>
        </div>
      </div>
    </div>

    <div v-if="rule.frequency === 'yearly' && showMonths" class="nx-recurrence-input__block">
      <span :id="`${uid}-months`" class="nx-recurrence-input__caption">{{ text.months }}</span>
      <div class="nx-recurrence-input__months" role="group" :aria-labelledby="`${uid}-months`">
        <NxChip
          v-for="month in monthChips"
          :key="month.value"
          class="nx-recurrence-input__month"
          size="s"
          :selected="(rule.byMonth ?? []).includes(month.value)"
          :aria-pressed="(rule.byMonth ?? []).includes(month.value)"
          :disabled="disabled"
          @click="toggleMonth(month.value)"
        >
          {{ month.label }}
        </NxChip>
      </div>
    </div>

    <div v-if="active" class="nx-recurrence-input__block">
      <NxToggleGroup
        :model-value="end"
        :options="endOptions"
        :label="text.end"
        :disabled="disabled"
        data-part="end"
        @update:model-value="setEnd"
      />
      <div v-if="end === 'count'" class="nx-recurrence-input__field is-number" data-part="count">
        <NxLabel :text="text.count">
          <NxNumberInput
            :model-value="rule.count ?? ''"
            :pattern="UINT_PATTERN_NO_ZERO"
            inputmode="numeric"
            :disabled="disabled"
            @update:model-value="setCount"
          />
        </NxLabel>
      </div>
      <div v-else-if="end === 'until'" class="nx-recurrence-input__field" data-part="until">
        <NxLabel :text="text.until">
          <NxDateInput
            :model-value="rule.until ?? null"
            :min-value="startIso"
            :locale="locale"
            :disabled="disabled"
            @update:model-value="setUntil"
          />
        </NxLabel>
      </div>
    </div>

    <p
      v-if="showSummary && active && summaryText"
      class="nx-recurrence-input__summary"
      aria-live="polite"
    >
      <Repeat class="nxt-icon-sm" aria-hidden="true" />
      <span>{{ summaryText }}</span>
    </p>

    <div v-if="active && occurrenceLines.length > 0" class="nx-recurrence-input__block">
      <span :id="`${uid}-next`" class="nx-recurrence-input__caption">
        {{ text.occurrences }}
      </span>
      <ul class="nx-recurrence-input__occurrences" :aria-labelledby="`${uid}-next`">
        <li v-for="(line, index) in occurrenceLines" :key="`${line.key}-${index}`">
          {{ line.text }}
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.nx-recurrence-input {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
}
.nx-recurrence-input__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--nxt-space-sm);
  min-width: 0;
}
.nx-recurrence-input__field {
  flex: 1 1 calc(var(--nxt-size-unit) * 10);
  min-width: 0;
}
.nx-recurrence-input__field.is-number {
  flex: 0 0 calc(var(--nxt-size-unit) * 5);
}
.nx-recurrence-input__unit {
  padding-block-end: var(--nxt-space-xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}
.nx-recurrence-input__block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
.nx-recurrence-input__block > .nx-recurrence-input__row,
.nx-recurrence-input__block > .nx-recurrence-input__field:not(.is-number) {
  align-self: stretch;
}
.nx-recurrence-input__block > .nx-recurrence-input__field.is-number {
  width: calc(var(--nxt-size-unit) * 5);
}
.nx-recurrence-input__caption {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-recurrence-input__months {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs);
}
.nx-recurrence-input__month {
  min-width: calc(var(--nxt-size-unit) * 2.5);
  justify-content: center;
}
.nx-recurrence-input__summary {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
  /* The global type reset sizes every bare <p> at 16px medium. */
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-regular);
}
.nx-recurrence-input__occurrences {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding-inline-start: var(--nxt-space-md);
  color: var(--nxt-fg-muted);
  /* The global type reset sizes every bare <ul> at 16px medium. */
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-regular);
}
.nx-recurrence-input__summary > svg {
  flex: none;
  color: var(--nxt-brand-bg);
}
</style>
